'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdBiotech,
    MdClose,
    MdCheckCircle,
    MdCancel,
    MdRefresh,
    MdImage,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import {
    getPacsServers,
    deletePacsServer,
    testPacsServer,
    getPacsServersByCompany,
    PacsServer,
} from '@/lib/api/health-integrations/pacs-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { MODULE_LABELS } from '@/hooks/useShareData'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { MdFileDownload } from 'react-icons/md'

export default function PacsPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'health_integrations',
        'pacs'
    )

    const [servers, setServers] = useState<PacsServer[]>([])
    const [filteredServers, setFilteredServers] = useState<PacsServer[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [serverToDelete, setServerToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [testing, setTesting] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadServers()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = servers.filter(
                server =>
                    server.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    server.aeTitle
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    server.host.toLowerCase().includes(searchTerm.toLowerCase())
            )
            setFilteredServers(filtered)
        } else {
            setFilteredServers(servers)
        }
    }, [searchTerm, servers])

    const loadServers = async () => {
        setLoading(true)
        try {
            const response = await getPacsServers()
            if (response.success) {
                setServers(response.result)
                setFilteredServers(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar servidores PACS'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar servidores PACS')
        } finally {
            setLoading(false)
        }
    }

    const handleTest = async (id: number) => {
        setTesting(id)
        try {
            const response = await testPacsServer(id)
            if (response.success && response.result.success) {
                toast.success('Conexão testada com sucesso')
            } else {
                toast.error(
                    response.result?.message || 'Falha no teste de conexão'
                )
            }
        } catch (error) {
            toast.error('Erro ao testar conexão')
        } finally {
            setTesting(null)
        }
    }

    const handleDelete = (id: number) => {
        setServerToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!serverToDelete) return

        setDeleting(true)
        try {
            const response = await deletePacsServer(serverToDelete)
            if (response.success) {
                toast.success('Servidor excluído com sucesso')
                loadServers()
                setShowDeleteModal(false)
                setServerToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir servidor')
            }
        } catch (error) {
            toast.error('Erro ao excluir servidor')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando servidores PACS...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdBiotech className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            PACS/DICOM
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {servers.length}{' '}
                            {servers.length === 1 ? 'servidor' : 'servidores'}{' '}
                            configurados
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() =>
                            navigate('/health-integrations/pacs/studies')
                        }
                    >
                        <MdImage className="text-xl" />
                        <span className="hidden sm:inline">Estudos</span>
                    </button>
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={() => setShowImportModal(true)}
                    >
                        <MdFileDownload className="text-xl" />
                        <span className="hidden sm:inline">Importar</span>
                    </button>
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/health-integrations/pacs/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Adicionar</span>
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, AE Title ou host..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredServers.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBiotech className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum servidor encontrado'
                                : 'Nenhum servidor PACS configurado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Configure servidores PACS para visualizar imagens medicas'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/health-integrations/pacs/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar servidor
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {filteredServers.map(server => (
                        <div
                            key={server.id}
                            className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-start gap-4 p-4">
                                <div className="relative w-16 h-16 flex-shrink-0 bg-gradient-to-br from-orange-500/20 to-orange-500/10 rounded-xl flex items-center justify-center border border-orange-500/20">
                                    <MdBiotech className="text-3xl text-orange-500" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-bold text-base-content break-words">
                                                    {server.name}
                                                </h3>
                                                {server.active ? (
                                                    <span className="badge badge-success badge-sm gap-1">
                                                        <MdCheckCircle className="text-xs" />
                                                        Ativo
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-ghost badge-sm gap-1">
                                                        <MdCancel className="text-xs" />
                                                        Inativo
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-base-content/70 mt-1 font-mono">
                                                {server.host}:{server.port} (AE:{' '}
                                                {server.aeTitle})
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mb-3">
                                        {server.wadoRsUrl && (
                                            <span className="badge badge-outline badge-sm">
                                                WADO-RS
                                            </span>
                                        )}
                                        {server.qidoRsUrl && (
                                            <span className="badge badge-outline badge-sm">
                                                QIDO-RS
                                            </span>
                                        )}
                                        {server.stowRsUrl && (
                                            <span className="badge badge-outline badge-sm">
                                                STOW-RS
                                            </span>
                                        )}
                                        {server._count && (
                                            <span className="badge badge-outline badge-sm">
                                                {server._count.studies} estudos
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                        <div className="flex items-center gap-2 text-xs text-base-content/60">
                                            <span>
                                                Criado em{' '}
                                                {new Date(
                                                    server.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() =>
                                                    handleTest(server.id)
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title="Testar conexão"
                                                disabled={testing === server.id}
                                            >
                                                {testing === server.id ? (
                                                    <span className="loading loading-spinner loading-sm"></span>
                                                ) : (
                                                    <MdRefresh className="text-lg" />
                                                )}
                                            </button>
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/health-integrations/pacs/edit/${server.id}`
                                                        )
                                                    }
                                                    className="btn btn-sm btn-ghost"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(server.id)
                                                    }
                                                    className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.PACS_SERVER}
                moduleLabel={MODULE_LABELS[ShareableModule.PACS_SERVER]}
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getPacsServersByCompany(companyId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: PacsServer) => (
                    <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-base-content/60">
                            {item.host}:{item.port} (AE: {item.aeTitle})
                            {item.active && (
                                <span className="badge badge-success badge-xs ml-2">
                                    Ativo
                                </span>
                            )}
                        </div>
                    </div>
                )}
                getItemId={(item: PacsServer) => item.id}
                getItemName={(item: PacsServer) => item.name}
                onImportComplete={() => loadServers()}
            />

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setServerToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir servidor"
                message="Tem certeza de que deseja excluir este servidor PACS?"
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
